import { hasForeignerUnreadCommentsInDocument } from '@core/document-comments-utils';
import { Action, ActionReducer, createReducer, on } from '@ngrx/store';
import * as commentsActions from '@state/comments/comments.actions';
import * as userProcessActions from './user-process.actions';
import { UserProcessState, initialState } from './user-process.state';

const reducer: ActionReducer<UserProcessState, Action> = createReducer(
  initialState,

  on(
    userProcessActions.getMyProcesses,
    (state): UserProcessState => ({
      ...state,
      loadingMyProcesses: true,
      errorMessage: null,
    })
  ),
  on(
    userProcessActions.getMyProcessesSuccess,
    (state, { myProcesses }): UserProcessState => ({
      ...state,
      myProcesses,
      loadingMyProcesses: false,
      errorMessage: null,
    })
  ),
  on(
    userProcessActions.getMyProcessesError,
    (state, { errorMessage }): UserProcessState => ({
      ...state,
      errorMessage,
      loadingMyProcesses: false,
    })
  ),

  on(
    userProcessActions.getMyProcessDetails,
    (state): UserProcessState => ({
      ...state,
      myProcess: null,
      loadingProcess: true,
      processDocuments: null,
      errorMessage: null,
    })
  ),
  on(
    userProcessActions.getMyProcessDetailsSuccess,
    (state, { myProcess }): UserProcessState => ({
      ...state,
      myProcess,
      loadingProcess: false,
      errorMessage: null,
    })
  ),
  on(
    userProcessActions.getMyProcessDetailsError,
    (state, { errorMessage }): UserProcessState => ({
      ...state,
      errorMessage,
      loadingProcess: false,
    })
  ),

  on(
    userProcessActions.addNewUserProcess,
    (state, {}): UserProcessState => ({
      ...state,
      loading: true,
      loadingMyProcesses: true,
      errorMessage: null,
    })
  ),
  on(
    userProcessActions.addNewUserProcessSuccess,
    (state, { addedUserProcess }): UserProcessState => ({
      ...state,
      myProcesses: [...state.myProcesses, addedUserProcess],
      processDocuments: null,
      loadingMyProcesses: false,
      loading: false,
      errorMessage: null,
    })
  ),
  on(
    userProcessActions.addNewUserProcessError,
    (state, { errorMessage }): UserProcessState => ({
      ...state,
      loading: false,
      loadingMyProcesses: false,
      errorMessage,
    })
  ),

  on(
    userProcessActions.pickProcess,
    (state): UserProcessState => ({
      ...state,
      loading: true,
      errorMessage: null,
    })
  ),
  on(
    userProcessActions.pickProcessSuccess,
    (state, { userProcess: myProcess }): UserProcessState => ({
      ...state,
      loading: false,
      errorMessage: null,
      myProcess: { ...myProcess },
    })
  ),
  on(
    userProcessActions.pickProcessError,
    (state, { errorMessage }): UserProcessState => ({
      ...state,
      errorMessage,
      loading: false,
    })
  ),

  on(
    userProcessActions.updatePreSurvey,
    (state): UserProcessState => ({
      ...state,
      loading: true,
      errorMessage: null,
    })
  ),
  on(
    userProcessActions.updatePreSurveySuccess,
    (state, { preSurvey, userId, userProcessId }): UserProcessState => ({
      ...state,
      myProcess:
        userProcessId === state.myProcess?.id
          ? {
              ...state.myProcess,
              preSurvey: {
                ...state.myProcess.preSurvey,
                ...preSurvey,
              },
            }
          : { ...state.myProcess },
      myProcesses: state.myProcesses.map(processInState => {
        return processInState.id === userProcessId
          ? {
              ...processInState,
              preSurvey: { ...processInState.preSurvey, ...preSurvey },
            }
          : processInState;
      }),
      loading: false,
      errorMessage: null,
    })
  ),
  on(
    userProcessActions.updatePreSurveyError,
    (state): UserProcessState => ({
      ...state,
      loading: false,
    })
  ),

  on(
    userProcessActions.sendPreSurveyForVerification,
    (state): UserProcessState => ({
      ...state,
      loading: true,
      errorMessage: null,
    })
  ),
  on(
    userProcessActions.sendPreSurveyForVerificationSuccess,
    (state, { userProcess }): UserProcessState => ({
      ...state,
      myProcess: userProcess.id === state.myProcess?.id ? { ...userProcess } : state.myProcess,
      myProcesses: state.myProcesses.map(processInState => {
        return processInState.id === userProcess.id ? { ...userProcess } : processInState;
      }),
      loading: false,
      errorMessage: null,
    })
  ),
  on(
    userProcessActions.sendPreSurveyForVerificationError,
    (state): UserProcessState => ({
      ...state,
      loading: false,
    })
  ),

  on(
    userProcessActions.updatePersonalDetails,
    (state): UserProcessState => ({
      ...state,
      loading: true,
      errorMessage: null,
    })
  ),
  on(
    userProcessActions.updatePersonalDetailsSuccess,
    (state, { updatedUserProcess }): UserProcessState => ({
      ...state,
      myProcess: { ...updatedUserProcess },
      myProcesses: state.myProcesses.map(userProcessInState => {
        return userProcessInState.id === updatedUserProcess.id ? updatedUserProcess : userProcessInState;
      }),
      loading: false,
      errorMessage: null,
    })
  ),
  on(
    userProcessActions.updatePersonalDetailsError,
    (state, { errorMessage }): UserProcessState => ({
      ...state,
      errorMessage,
      loading: false,
    })
  ),

  on(
    userProcessActions.confirmPersonalDetails,
    (state): UserProcessState => ({
      ...state,
      loading: true,
      errorMessage: null,
      lastDetailsValidation: null,
    })
  ),
  on(
    userProcessActions.confirmPersonalDetailsSuccess,
    (state, { validationResult }): UserProcessState => ({
      ...state,
      loading: false,
      errorMessage: null,
      lastDetailsValidation: validationResult,
    })
  ),
  on(
    userProcessActions.confirmPersonalDetailsError,
    (state, { errorMessage }): UserProcessState => ({
      ...state,
      errorMessage,
      loading: false,
      lastDetailsValidation: null,
    })
  ),

  on(
    userProcessActions.startCompletingDocuments,
    userProcessActions.sendAppToReview,
    (state): UserProcessState => ({
      ...state,
      processDocuments: null,
      loading: true,
      errorMessage: null,
    })
  ),
  on(
    userProcessActions.startCompletingDocumentsSuccess,
    userProcessActions.sendAppToReviewSuccess,
    (state, { updatedUserProcess }): UserProcessState => ({
      ...state,
      myProcess: updatedUserProcess,
      myProcesses: state.myProcesses.map(userProcess => {
        return userProcess.id === updatedUserProcess.id ? updatedUserProcess : userProcess;
      }),
      loading: false,
      errorMessage: null,
    })
  ),
  on(
    userProcessActions.startCompletingDocumentsError,
    userProcessActions.sendAppToReviewError,
    (state, { errorMessage }): UserProcessState => ({
      ...state,
      errorMessage,
      loading: false,
    })
  ),

  on(
    userProcessActions.getDocumentsList,
    (state): UserProcessState => ({
      ...state,
      loading: true,
      errorMessage: null,
    })
  ),
  on(
    userProcessActions.getDocumentsListSuccess,
    (state, { documentsList }): UserProcessState => ({
      ...state,
      loading: false,
      errorMessage: null,
      processDocuments: documentsList,
    })
  ),
  on(
    userProcessActions.getDocumentsListError,
    (state, { errorMessage }): UserProcessState => ({
      ...state,
      errorMessage,
      loading: false,
    })
  ),
  on(
    userProcessActions.addFilesToMyDocument,
    userProcessActions.answerToMyDocQuestion,
    userProcessActions.saveUserDocument,
    (state): UserProcessState => ({
      ...state,
      loading: true,
      errorMessage: null,
    })
  ),
  on(
    userProcessActions.answerToMyDocQuestionSuccess,
    userProcessActions.addFilesToMyDocumentSuccess,
    userProcessActions.saveUserDocumentSuccess,
    (state, { userDocument }): UserProcessState => {
      const docExistedBefore = state.processDocuments.userDocs.find(doc => doc.id === userDocument.id);
      let updatedUserDocs = [];
      if (docExistedBefore) {
        updatedUserDocs = state.processDocuments.userDocs.map(doc => (doc.id === userDocument.id ? userDocument : doc));
      } else {
        updatedUserDocs = [...state.processDocuments.userDocs, userDocument];
      }
      return {
        ...state,
        loading: false,
        processDocuments: {
          processDocs: [...state.processDocuments.processDocs],
          userDocs: updatedUserDocs,
        },
        errorMessage: null,
      };
    }
  ),
  on(
    userProcessActions.answerToMyDocQuestionError,
    userProcessActions.addFilesToMyDocumentError,
    (state): UserProcessState => ({
      ...state,
      loading: false,
    })
  ),
  on(
    userProcessActions.saveUserDocumentError,
    (state, { errorMessage }): UserProcessState => ({
      ...state,
      errorMessage,
      loading: false,
    })
  ),

  on(
    userProcessActions.downloadFile,
    (state, { userDocumentId, filename }): UserProcessState => ({
      ...state,
      loading: true,
      errorMessage: null,
      downloadingUserDocumentFile: { userDocumentId, filename },
    })
  ),
  on(
    userProcessActions.downloadFileSuccess,
    (state): UserProcessState => ({
      ...state,
      loading: false,
      errorMessage: null,
      downloadingUserDocumentFile: { userDocumentId: null, filename: null },
    })
  ),
  on(
    userProcessActions.downloadFileError,
    (state): UserProcessState => ({
      ...state,
      loading: false,
      downloadingUserDocumentFile: { userDocumentId: null, filename: null },
    })
  ),

  on(
    userProcessActions.removeUserDoc,
    (state): UserProcessState => ({
      ...state,
      loading: true,
      errorMessage: null,
    })
  ),
  on(
    userProcessActions.removeUserDocSuccess,
    (state, { removedUserDocId }): UserProcessState => ({
      ...state,
      loading: false,
      errorMessage: null,
      processDocuments: {
        processDocs: [...state.processDocuments.processDocs],
        userDocs: state.processDocuments.userDocs.filter(userDoc => userDoc.id !== removedUserDocId),
      },
    })
  ),
  on(
    userProcessActions.removeUserDocError,
    (state, { errorMessage }): UserProcessState => ({
      ...state,
      errorMessage,
      loading: false,
    })
  ),

  on(
    userProcessActions.removeFileOfUserDoc,
    (state): UserProcessState => ({
      ...state,
      loading: true,
      errorMessage: null,
    })
  ),
  on(
    userProcessActions.removeFileOfUserDocSuccess,
    (state, { updatedUserDoc }): UserProcessState => ({
      ...state,
      loading: false,
      errorMessage: null,
      processDocuments: {
        processDocs: [...state.processDocuments.processDocs],
        userDocs: state.processDocuments.userDocs.map(userDoc => {
          if (userDoc.id === updatedUserDoc.id) {
            return updatedUserDoc;
          }
          return userDoc;
        }),
      },
    })
  ),
  on(
    userProcessActions.removeFileOfUserDocError,
    (state, { errorMessage }): UserProcessState => ({
      ...state,
      errorMessage,
      loading: false,
    })
  ),

  on(
    userProcessActions.saveAdditionalDocs,
    (state): UserProcessState => ({
      ...state,
      loading: true,
      errorMessage: null,
    })
  ),
  on(userProcessActions.saveAdditionalDocsSuccess, (state, { savedUserDocuments }): UserProcessState => {
    const additionalDocId = savedUserDocuments[0].documentId;
    const userNormalDocs = state.processDocuments.userDocs.filter(userDoc => userDoc.documentId !== additionalDocId);
    const userAdditionalDocs = [
      ...state.processDocuments.userDocs.filter(
        oldUserDoc => !savedUserDocuments.find(newUserDoc => newUserDoc.id === oldUserDoc.id)
      ),
      ...savedUserDocuments,
    ];

    return {
      ...state,
      processDocuments: {
        ...state.processDocuments,
        userDocs: [...userNormalDocs, ...userAdditionalDocs],
      },
      loading: false,
      errorMessage: null,
    };
  }),

  on(
    userProcessActions.getDocumentsForSummary,
    (state): UserProcessState => ({
      ...state,
      summaryDocuments: null,
      loading: true,
      errorMessage: null,
    })
  ),
  on(
    userProcessActions.getDocumentsForSummarySuccess,
    (state, { summaryDocuments }): UserProcessState => ({
      ...state,
      summaryDocuments,
      loading: false,
      errorMessage: null,
    })
  ),
  on(
    userProcessActions.getDocumentsForSummaryError,
    (state, { errorMessage }): UserProcessState => ({
      ...state,
      errorMessage,
      loading: false,
    })
  ),

  on(
    commentsActions.markDocumentCommentsAsReadSuccess,
    (state, { userDocumentId, userProcessId, updatedComments }): UserProcessState => {
      // if no myProcess loaded
      if (state.myProcess?.id !== userProcessId) {
        return state;
      }

      // if no userDocuments loaded
      if (!state.processDocuments?.userDocs?.length) {
        return state;
      }

      const updatedUserDocs = state.processDocuments.userDocs.map(userDoc => {
        if (userDoc.id !== userDocumentId) {
          return { ...userDoc };
        }

        return { ...userDoc, documentComments: updatedComments };
      });

      return {
        ...state,
        processDocuments: {
          ...state.processDocuments,
          userDocs: updatedUserDocs,
        },
      };
    }
  ),

  on(
    commentsActions.readerAnyNewCommentsForceCheck,
    (state, { readerId, userProcessId, userDocumentId, updatedComments }): UserProcessState => {
      if (state.myProcess?.id !== userProcessId) {
        return { ...state };
      }

      const userDocs = state.processDocuments?.userDocs;
      if (!userDocs?.length) {
        return { ...state };
      }

      const hasNewMessages = hasForeignerUnreadCommentsInDocument(readerId, updatedComments);
      return {
        ...state,
        processDocuments: {
          ...state.processDocuments,
          userDocs: state.processDocuments.userDocs.map(userDoc => {
            return userDoc.id === userDocumentId ? { ...userDoc, hasNewMessages } : { ...userDoc };
          }),
        },
      };
    }
  ),

  on(
    userProcessActions.downloadForPrintAll,
    userProcessActions.downloadInstructions,
    (state): UserProcessState => ({
      ...state,
      loading: true,
      errorMessage: null,
    })
  ),
  on(
    userProcessActions.downloadForPrintAllSuccess,
    userProcessActions.downloadInstructionsSuccess,
    (state): UserProcessState => ({
      ...state,
      loading: false,
      errorMessage: null,
    })
  ),

  on(
    userProcessActions.downloadForPrintAllError,
    userProcessActions.downloadInstructionsError,
    (state): UserProcessState => ({
      ...state,
      loading: false,
      errorMessage: null,
    })
  ),

  on(
    userProcessActions.saveDocumentsSigningMode,
    (state): UserProcessState => ({
      ...state,
      errorMessage: null,
      loading: true,
    })
  ),
  on(
    userProcessActions.saveDocumentsSigningModeSuccess,
    (state, { userProcess }): UserProcessState => ({
      ...state,
      errorMessage: null,
      loading: false,
      myProcess: { ...userProcess },
    })
  ),
  on(
    userProcessActions.saveDocumentsSigningModeError,
    (state, { errorMessage }): UserProcessState => ({
      ...state,
      errorMessage,
      loading: false,
    })
  ),

  on(
    userProcessActions.saveMyAvailability,
    (state): UserProcessState => ({
      ...state,
      errorMessage: null,
      loading: true,
    })
  ),
  on(
    userProcessActions.saveMyAvailabilitySuccess,
    (state, { newUserProcessStatus, updatedUserProcess, userProcessId }): UserProcessState => ({
      ...state,
      errorMessage: null,
      loading: false,
      myProcess: {
        ...state.myProcess,
        status: newUserProcessStatus,
        availabilityForOfficeVisit: updatedUserProcess.availabilityForOfficeVisit,
      },
    })
  ),
  on(
    userProcessActions.saveMyAvailabilityError,
    (state, { errorMessage }): UserProcessState => ({
      ...state,
      errorMessage,
      loading: false,
    })
  ),
  on(
    userProcessActions.confirmVoivoChange,
    (state): UserProcessState => ({
      ...state,
      loading: true,
      errorMessage: null,
    })
  ),
  on(
    userProcessActions.confirmVoivoChangeSuccess,
    (state): UserProcessState => ({
      ...state,
      loading: false,
      errorMessage: null,
    })
  ),
  on(
    userProcessActions.confirmVoivoChangeError,
    (state, { errorMessage }): UserProcessState => ({
      ...state,
      errorMessage,
      loading: false,
    })
  ),

  on(
    userProcessActions.setReadinessForVisit,
    (state, { opts }): UserProcessState => ({
      ...state,
      loading: true,
    })
  ),
  on(
    userProcessActions.setReadinessForVisitSuccess,
    (state, { userProcess }): UserProcessState => ({
      ...state,
      loading: false,
      errorMessage: null,
      myProcess: userProcess.id === state.myProcess.id ? { ...userProcess } : state.myProcess,
    })
  ),
  on(
    userProcessActions.setReadinessForVisitError,
    (state, { errorMessage }): UserProcessState => ({
      ...state,
      errorMessage,
      loading: false,
    })
  ),

  on(
    userProcessActions.setAppointmentDateAndPlace,
    (state, { opts }): UserProcessState => ({
      ...state,
      loading: true,
    })
  ),
  on(
    userProcessActions.setAppointmentDateAndPlaceSuccess,
    (state, { userProcess }): UserProcessState => ({
      ...state,
      loading: false,
      errorMessage: null,
      myProcess: userProcess.id === state.myProcess.id ? { ...userProcess } : state.myProcess,
      myProcesses: state.myProcesses?.length
        ? state.myProcesses.map(mp => (mp.id === userProcess.id ? { ...userProcess } : mp))
        : state.myProcesses,
    })
  ),
  on(
    userProcessActions.setAppointmentDateAndPlaceError,
    (state): UserProcessState => ({
      ...state,
      loading: false,
    })
  ),

  on(
    userProcessActions.uploadDecision,
    (state): UserProcessState => ({
      ...state,
      loading: true,
    })
  ),
  on(
    userProcessActions.uploadDecisionSuccess,
    (state, { userProcess }): UserProcessState => ({
      ...state,
      loading: false,
      errorMessage: null,
      myProcess: userProcess.id === state.myProcess.id ? { ...userProcess } : state.myProcess,
      myProcesses: state.myProcesses?.length
        ? state.myProcesses.map(mp => (mp.id === userProcess.id ? { ...userProcess } : mp))
        : state.myProcesses,
    })
  ),
  on(
    userProcessActions.uploadDecisionError,
    (state): UserProcessState => ({
      ...state,
      loading: false,
    })
  ),

  on(
    userProcessActions.confirmMOSDraftPreparation,
    (state): UserProcessState => ({
      ...state,
      loading: true,
    })
  ),
  on(
    userProcessActions.confirmMOSDraftPreparationSuccess,
    (state, { userProcess }): UserProcessState => ({
      ...state,
      loading: false,
      errorMessage: null,
      myProcess: userProcess.id === state.myProcess.id ? { ...userProcess } : state.myProcess,
      myProcesses: state.myProcesses?.length
        ? state.myProcesses.map(mp => (mp.id === userProcess.id ? { ...userProcess } : mp))
        : state.myProcesses,
    })
  ),
  on(
    userProcessActions.confirmMOSDraftPreparationError,
    (state, { errorMessage }): UserProcessState => ({
      ...state,
      errorMessage,
      loading: false,
    })
  ),

  on(
    userProcessActions.confirmSubmissionOfApplication,
    (state): UserProcessState => ({
      ...state,
      loading: true,
    })
  ),
  on(
    userProcessActions.confirmSubmissionOfApplicationSuccess,
    (state, { userProcess }): UserProcessState => ({
      ...state,
      loading: false,
      errorMessage: null,
      myProcess: userProcess.id === state.myProcess.id ? { ...userProcess } : state.myProcess,
      myProcesses: state.myProcesses?.length
        ? state.myProcesses.map(mp => (mp.id === userProcess.id ? { ...userProcess } : mp))
        : state.myProcesses,
    })
  ),
  on(
    userProcessActions.confirmSubmissionOfApplicationError,
    (state): UserProcessState => ({
      ...state,
      loading: false,
    })
  ),

  on(
    userProcessActions.confirmSendingByPost,
    (state): UserProcessState => ({
      ...state,
      loading: true,
    })
  ),
  on(
    userProcessActions.confirmSendingByPostSuccess,
    (state, { userProcess }): UserProcessState => ({
      ...state,
      loading: false,
      errorMessage: null,
      myProcess: userProcess.id === state.myProcess.id ? { ...userProcess } : state.myProcess,
      myProcesses: state.myProcesses?.length
        ? state.myProcesses.map(mp => (mp.id === userProcess.id ? { ...userProcess } : mp))
        : state.myProcesses,
    })
  ),
  on(
    userProcessActions.confirmSendingByPostError,
    (state): UserProcessState => ({
      ...state,
      loading: false,
    })
  )
);

export function userProcessReducer(state: UserProcessState, action: Action): UserProcessState {
  return reducer(state, action);
}
